import SearchAPI from '../../api/search';
import types from '../mutation-types';

const PER_PAGE = 15;

// Paginated search responses can overlap across pages (new records shift
// offsets between fetches), so drop records that are already in the list.
const appendUniqueRecords = (existingRecords, newRecords) => {
  const existingIds = new Set(existingRecords.map(record => record.id));
  return [
    ...existingRecords,
    ...newRecords.filter(record => !existingIds.has(record.id)),
  ];
};
const pendingRequests = new WeakMap();

const cancelRequests = state => {
  pendingRequests.get(state)?.forEach(controller => controller.abort());
  pendingRequests.delete(state);
};

const beginRequest = (state, kind) => {
  let requests = pendingRequests.get(state);
  if (!requests) {
    requests = new Map();
    pendingRequests.set(state, requests);
  }
  requests.get(kind)?.abort();
  const controller = new AbortController();
  requests.set(kind, controller);
  const generation = state.searchGeneration;
  const accountId = SearchAPI.accountIdFromRoute;
  return {
    signal: controller.signal,
    current: () => !controller.signal.aborted &&
      generation === state.searchGeneration &&
      accountId === SearchAPI.accountIdFromRoute &&
      requests.get(kind) === controller,
  };
};

const pagedSearch = (kind, recordType, flagType) => async ({ state, commit }, payload) => {
  const { page = 1, ...searchParams } = payload;
  const request = beginRequest(state, kind);
  commit(flagType, { isFetching: true, error: false, errorCode: null });
  try {
    const { data } = await SearchAPI[`${kind}s`]({ ...searchParams, page, signal: request.signal });
    if (!request.current()) return null;
    const records = data.payload[`${kind}s`];
    commit(recordType, records);
    commit(flagType, { hasMore: records.length === PER_PAGE });
    return true;
  } catch (error) {
    if (!request.current()) return null;
    commit(flagType, { error: true, errorCode: error?.response?.data?.code || null });
    return false;
  } finally {
    if (request.current()) commit(flagType, { isFetching: false });
  }
};

export const initialState = {
  searchGeneration: 0,
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  articleRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ state, commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    const request = beginRequest(state, 'all');
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: Boolean(q), error: false });
    if (!q) return;
    try {
      const { data: { payload } } = await SearchAPI.get({ q, signal: request.signal });
      if (request.current()) commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      if (request.current()) commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { error: true });
    } finally {
      if (request.current()) commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: false });
    }
  },
  async fullSearch({ state, commit, dispatch }, payload) {
    cancelRequests(state);
    commit(types.CLEAR_SEARCH_RESULTS);
    const { q, ...filters } = payload;
    if (!q && !Object.keys(filters).length) return;
    const generation = state.searchGeneration;
    const accountId = SearchAPI.accountIdFromRoute;
    commit(types.FULL_SEARCH_SET_UI_FLAG, { isFetching: true, isSearchCompleted: false });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, ...filters }),
        dispatch('conversationSearch', { q, ...filters }),
        dispatch('messageSearch', { q, ...filters }),
        dispatch('articleSearch', { q, ...filters }),
      ]);
    } finally {
      if (generation === state.searchGeneration && accountId === SearchAPI.accountIdFromRoute) {
        commit(types.FULL_SEARCH_SET_UI_FLAG, { isFetching: false, isSearchCompleted: true });
      }
    }
  },
  contactSearch: pagedSearch('contact', types.CONTACT_SEARCH_SET, types.CONTACT_SEARCH_SET_UI_FLAG),
  conversationSearch: pagedSearch('conversation', types.CONVERSATION_SEARCH_SET, types.CONVERSATION_SEARCH_SET_UI_FLAG),
  messageSearch: pagedSearch('message', types.MESSAGE_SEARCH_SET, types.MESSAGE_SEARCH_SET_UI_FLAG),
  articleSearch: pagedSearch('article', types.ARTICLE_SEARCH_SET, types.ARTICLE_SEARCH_SET_UI_FLAG),
  clearSearchResults({ state, commit }) {
    cancelRequests(state);
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = appendUniqueRecords(state.contactRecords, records);
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = appendUniqueRecords(
      state.conversationRecords,
      records
    );
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = appendUniqueRecords(state.messageRecords, records);
  },
  [types.ARTICLE_SEARCH_SET](state, records) {
    state.articleRecords = appendUniqueRecords(state.articleRecords, records);
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.searchGeneration += 1;
    state.records = [];
    state.uiFlags = {
      isFetching: false, isSearchCompleted: false,
      contact: { isFetching: false, hasMore: false, error: false },
      conversation: { isFetching: false, hasMore: false, error: false },
      message: { isFetching: false, hasMore: false, error: false },
      article: { isFetching: false, hasMore: false, error: false },
    };
    state.contactRecords = [];
    state.conversationRecords = [];
    state.messageRecords = [];
    state.articleRecords = [];
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
